<template>
  <div class="px-3 py-2 border-b border-white/5">
    <label class="block text-[10px] uppercase tracking-widest text-text-muted font-semibold mb-1">
      {{ $t('chat.friends.add_by_id_label') }}
    </label>
    <form class="flex items-center gap-2" @submit.prevent="submit">
      <input
        ref="inputEl"
        v-model="input"
        type="text"
        inputmode="latin"
        autocapitalize="characters"
        :placeholder="$t('chat.friends.add_by_id_placeholder')"
        :disabled="busy"
        maxlength="9"
        class="flex-1 px-2 py-1.5 rounded-md bg-black/30 border border-white/5 text-sm font-mono tracking-widest text-white placeholder:text-text-dim outline-none focus:border-primary/50 transition-colors uppercase"
      />
      <button
        type="submit"
        class="shrink-0 px-3 py-1.5 rounded-md bg-primary text-black text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
        :disabled="!canSubmit"
      >
        {{ busy ? '…' : $t('chat.friends.add_by_id_submit') }}
      </button>
    </form>
    <p v-if="hint" class="mt-1 text-[10px]" :class="hintClass">{{ hint }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useFriends } from '~/composables/chat/useFriends'

/**
 * "Add by Chat ID" input — complements the name-search bar in the Friends
 * tab. Accepts the Crockford-Base32 chatId in either XXXX-XXXX or XXXXXXXX
 * form; normalises before submitting.
 *
 * The lookup + request are a single round-trip (`/api/chat/friends/request`
 * accepts `chatId`). On success we emit `added` so the parent can flash a
 * success toast; on failure we show an inline hint.
 */

const emit = defineEmits(['added'])
const { t } = useI18n()

const { sendRequest } = useFriends()

const input = ref('')
const busy = ref(false)
const hint = ref('')
const hintClass = ref('text-text-dim')

// Strip anything that isn't a Crockford-Base32 char + uppercase
const normalized = computed(() =>
  input.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
)
const canSubmit = computed(() => !busy.value && normalized.value.length === 8)

watch(input, () => {
  // Auto-prettify while the user types: insert a dash after 4 chars
  const n = normalized.value
  if (n.length === 8 && !input.value.includes('-')) {
    input.value = n.slice(0, 4) + '-' + n.slice(4)
  }
  hint.value = ''
})

async function submit() {
  if (!canSubmit.value) return
  busy.value = true
  hint.value = ''
  try {
    const res = await sendRequest(normalized.value, { byChatId: true })
    if (!res.ok) {
      hint.value = mapError(res.error)
      hintClass.value = 'text-red-400'
      return
    }
    hint.value = res.autoAccepted
      ? t('chat.friends.add_by_id_auto_accept')
      : t('chat.friends.add_by_id_sent')
    hintClass.value = 'text-emerald-400'
    input.value = ''
    emit('added', { autoAccepted: res.autoAccepted })
  } finally {
    busy.value = false
  }
}

function mapError(code) {
  if (code === 'Blocked') return t('chat.friends.add_by_id_blocked')
  if (code === 'No user with that chat id') return t('chat.friends.add_by_id_not_found')
  if (code === 'That is your own chat id') return t('chat.friends.add_by_id_self')
  if (code === 'Invalid chat id format') return t('chat.friends.add_by_id_invalid')
  return t('chat.friends.add_by_id_generic_error')
}
</script>
